// Loan file top header — sticky bar with milestone stepper + metric strip.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { losApi, LOAN_STATUS_LABELS, LOAN_STATUS_ORDER } from '../../losApi';
import { downloadWithAuth } from '../../api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { ArrowLeft, Loader2, Link2, UserPlus, Download, Pencil } from 'lucide-react';
import { toast } from '../../../hooks/use-toast';
import { fmt$ } from './formFields';
import { BakeoffBadge } from './BakeoffBadge';
import { AssignmentPicker } from './AssignmentPicker';
import { AriveChips } from './AriveChips';
import AriveBadge from '../../board/AriveBadge';
import { ariveDeepLink, isAriveManaged } from '../../ariveManaged';
import { useAuth } from '../../AuthContext';
import { copyText } from '../../clipboard';
import { fromApi, isDateOnly } from '../../kit/time';
import { ltvStack } from './ltv';
import { loanDti } from './dti';
import { useBorrowerInvite } from './useBorrowerInvite';

export function LoanHeader({ loan, onStatus, saving, onOpenSection }) {
  const { user } = useAuth();
  const invite = useBorrowerInvite(loan.loan_file_id);
  const [assignOpen, setAssignOpen] = useState(false);
  const [mismoBusy, setMismoBusy] = useState(false);
  // Status is Arive-owned on a synced loan; the server answers 409 otherwise.
  const ariveLocked = isAriveManaged(loan);
  const canAssign = (user?.permissions || []).includes('loans.assign');
  // The export carries full SSNs; the server refuses it without ssn.reveal.
  const canExportMismo = (user?.permissions || []).includes('ssn.reveal');
  const b = loan.urla?.borrower || {};
  const name = [b.first_name, b.last_name].filter(Boolean).join(' ') || 'Unnamed borrower';
  const ltv = ltvStack(loan).ltv;
  const dti = loanDti(loan);
  const score = loan.estimated_credit_score || b.estimated_credit_score;
  const closeDate = loan.consummation_date ? fromApi(loan.consummation_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(isDateOnly(loan.consummation_date) ? { timeZone: 'UTC' } : {}) }) : '—';

  return (
    <div className="bg-white border-b border-slate-200 md:sticky md:top-14 md:z-30" data-testid="loan-header">
      <div className="max-w-[1600px] mx-auto px-4 md:px-6 pt-3">
        {/* Row 1: identity + right icons */}
        <div className="flex flex-wrap md:flex-nowrap items-center gap-x-3 gap-y-2 mb-2">
          <Link to="/portal/loans" data-testid="back-to-loans" className="text-slate-400 hover:text-slate-600">
            <ArrowLeft size={16} />
          </Link>
          <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
            {(loan.channel || 'Brokered').replace(/_/g, ' ')}
          </span>
          <span className="text-slate-300">/</span>
          <span className="font-mono text-[13px] font-semibold whitespace-nowrap" style={{ color: '#030524' }}>{loan.loan_number}</span>
          <span className="text-slate-300">/</span>
          <span className="text-[13px] font-semibold truncate min-w-0" style={{ color: '#030524' }}>{name}</span>

          <AssignmentPicker loanId={loan.loan_file_id} canAssign={canAssign} open={assignOpen} onOpenChange={setAssignOpen} />

          {ariveLocked && (
            <>
              <AriveBadge deepLink={ariveDeepLink(loan)} />
              <AriveChips arive={loan.arive} onAssign={canAssign ? () => setAssignOpen(true) : undefined} />
            </>
          )}

          <div className="hidden md:block flex-1" />

          <BakeoffBadge loan={loan} onOpen={() => onOpenSection && onOpenSection('loan_center')} />

          <StatusPill status={loan.status} onChange={onStatus} saving={saving} locked={ariveLocked} />

          <button
            data-testid="header-share-btn"
            onClick={async () => {
              if (await copyText(`${window.location.origin}/loan/${loan.share_token}`)) toast({ title: 'Public status link copied' });
              else toast({ title: 'Copy failed', description: 'Select the text and copy it manually' });
            }}
            className="w-8 h-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500"
            title="Copy public status link"
            aria-label="Copy public status link"
          >
            <Link2 size={14} />
          </button>
          {invite.canInvite && (
            <button
              data-testid="header-borrower-link-btn"
              onClick={invite.copyLink}
              className="w-8 h-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500"
              title="Copy borrower link"
              aria-label="Copy borrower link"
            >
              <UserPlus size={14} />
            </button>
          )}
          {canExportMismo && (
            <button
              data-testid="header-mismo-btn"
              onClick={async () => {
                setMismoBusy(true);
                try { await downloadWithAuth(losApi.mismoPath(loan.loan_file_id), `${loan.loan_number || loan.loan_file_id}_MISMO34.xml`); }
                finally { setMismoBusy(false); }
              }}
              disabled={mismoBusy}
              className="w-8 h-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500 disabled:opacity-50"
              title="MISMO 3.4 export"
              aria-label="MISMO 3.4 export"
            >
              {mismoBusy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            </button>
          )}
        </div>

        {/* Row 2: chevron milestone stepper */}
        <ChevronStepper current={loan.status} onChange={onStatus} locked={ariveLocked} />

        {/* Row 3: metric strip */}
        <div className="grid grid-cols-3 md:grid-cols-7 gap-x-6 gap-y-2 py-3 border-t border-slate-100">
          <Metric label="Loan Amount" value={fmt$(loan.loan_amount)} accent="#030524" testId="metric-loan-amount" />
          <Metric label="LTV" value={ltv ? `${ltv}%` : '—'} accent={ltv && ltv > 80 ? '#dc2626' : '#030524'} testId="metric-ltv" />
          <Metric label="Score" value={score || '—'} testId="metric-score" />
          <Metric label="Rate" value={loan.rate_lock?.rate ? `${loan.rate_lock.rate}%` : (loan.note_rate ? `${loan.note_rate}%` : '—')} testId="metric-rate" />
          <Metric label="DTI" value={dti.value != null ? `${dti.value}%` : '—'} sub={dti.source === 'estimate' ? 'est. from 1003' : ''} testId="metric-dti" />
          <Metric label="FTC" value={loan.qm?.ftc ? fmt$(loan.qm.ftc) : '—'} testId="metric-ftc" />
          <Metric label="Est. Closing" value={closeDate} onClick={onOpenSection ? () => onOpenSection('audit_dates') : undefined} editable={!ariveLocked} testId="metric-closing" />
        </div>
      </div>
    </div>
  );
}

function ChevronStepper({ current, onChange, locked = false }) {
  const currentIdx = LOAN_STATUS_ORDER.indexOf(current);
  const KEY_STEPS = [
    ['prospect', 'Prospect'],
    ['pre_qual', 'Pre-Qual'],
    ['pre_approved', 'Pre-Approved'],
    ['application_received', 'App Received'],
    ['processing', 'Processing'],
    ['submitted_to_uw', 'UW Review'],
    ['conditional_approval', 'Cond. Approval'],
    ['clear_to_close', 'Clear to Close'],
    ['closing_scheduled', 'Closing'],
    ['funded', 'Funded'],
  ];
  const currentPos = KEY_STEPS.findIndex(([s]) => s === current);
  const progressPct = currentPos >= 0 ? ((currentPos + 0.5) / KEY_STEPS.length) * 100 : 0;

  return (
    // Scrolls sideways on phones so the stage labels stay readable.
    <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 md:overflow-visible">
      <div className="relative py-3 min-w-[640px] md:min-w-0" data-testid="chevron-stepper">
        <div className="absolute left-0 right-0 top-[26px] h-[3px] rounded-full" style={{ background: 'linear-gradient(90deg, #e2e8f0 0%, #e2e8f0 100%)' }} />
        <div
          className="absolute left-0 top-[26px] h-[3px] rounded-full transition-all duration-500"
          style={{
            width: `${progressPct}%`,
            background: 'linear-gradient(90deg, #6BA82E 0%, #8bc94f 100%)',
            boxShadow: '0 0 12px rgba(107,168,46,0.5)',
          }}
        />
        <div className="flex items-start justify-between relative">
          {KEY_STEPS.map(([s, label]) => {
            const idx = LOAN_STATUS_ORDER.indexOf(s);
            const done = idx < currentIdx;
            const active = idx === currentIdx;
            return (
              <button
                key={s}
                onClick={() => onChange(s)}
                disabled={locked}
                title={locked ? 'Managed in Arive' : undefined}
                data-testid={`chevron-${s}`}
                className="flex flex-col items-center gap-1.5 group flex-1 min-w-0"
              >
                <span
                  className="w-3.5 h-3.5 rounded-full border-2 shrink-0 transition-all"
                  style={{
                    background: done || active ? '#6BA82E' : '#fff',
                    borderColor: done || active ? '#6BA82E' : '#cbd5e1',
                    boxShadow: active ? '0 0 0 4px rgba(107,168,46,0.2)' : 'none',
                  }}
                />
                <span
                  className={`text-[10px] font-semibold uppercase tracking-wider text-center leading-tight px-1 truncate w-full transition-colors ${
                    active ? '' : done ? '' : 'text-slate-400 group-hover:text-slate-600'
                  }`}
                  style={active || done ? { color: '#070B5F' } : {}}
                >
                  {label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status, onChange, saving, locked = false }) {
  return (
    <div className="relative inline-flex items-center gap-1">
      <Select value={status} onValueChange={onChange} disabled={saving || locked}>
        <SelectTrigger className="h-8 text-[12px] font-semibold w-44" data-testid="status-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {LOAN_STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{LOAN_STATUS_LABELS[s]}</SelectItem>)}
          <SelectItem value="cancelled">Cancelled</SelectItem>
          <SelectItem value="denied">Denied</SelectItem>
          <SelectItem value="withdrawn">Withdrawn</SelectItem>
        </SelectContent>
      </Select>
      {saving && <Loader2 className="animate-spin text-slate-400" size={12} />}
    </div>
  );
}

// With `onClick` the metric is a button; `editable` adds the pencil.
function Metric({ label, value, sub, accent = '#030524', testId, onClick, editable = false }) {
  const body = (
    <>
      <div className="text-[9px] uppercase tracking-widest text-slate-400 font-bold flex items-center gap-1">
        {label}
        {onClick && editable && <Pencil size={9} className="text-slate-300" />}
      </div>
      <div className="text-[15px] font-bold leading-tight truncate" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-[10px] text-slate-400">{sub}</div>}
    </>
  );
  if (!onClick) return <div className="min-w-0" data-testid={testId}>{body}</div>;
  return (
    <button type="button" onClick={onClick} className="min-w-0 text-left hover:opacity-80" data-testid={testId} aria-label={`${label}: ${value}. Open Audit & Dates`}>
      {body}
    </button>
  );
}
